import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ROUTE_CHANGE_EVENT, isCrossOrigin } from './_common';
import { addBlocker, currentBlocker } from './blocker';
import { createHistoryRoutingStrategy } from './history';
export { getRouteParams, getRouteParam, setRouteParam, makeRouteParamUrl, getEntryState, setEntryState, isCrossOrigin, carryOrigin } from './_common';
// Leaving the route on screen asks its blocker first. A move that stays on it (a tab, a filter, an
// anchor) goes straight through.
const withBlocker = (strategy) => (Object.assign(Object.assign({}, strategy), { 'navigate': (target, options) => {
        const blocker = currentBlocker();
        if (blocker === null || (options === null || options === void 0 ? void 0 : options.bypassBlocker) || !(isCrossOrigin(target) || strategy.parse(target).raw !== strategy.current().raw)) {
            strategy.navigate(target, options);
            return;
        }
        blocker(() => strategy.navigate(target, options));
    } }));
export const createRoutingStrategy = (basename) => withBlocker(createHistoryRoutingStrategy(basename));
export const onRouteChange = (f) => {
    window.addEventListener('popstate', f);
    window.addEventListener('hashchange', f);
    window.addEventListener(ROUTE_CHANGE_EVENT, f);
    return () => {
        window.removeEventListener('popstate', f);
        window.removeEventListener('hashchange', f);
        window.removeEventListener(ROUTE_CHANGE_EVENT, f);
    };
};
const RoutingStrategyContext = React.createContext({
    current: () => { throw 'not implemented'; },
    navigate: (_) => { throw 'not implemented'; },
    listen: (_) => { throw 'not implemented'; },
    pathToURL: (_) => { throw 'not implemented'; },
    parse: () => { throw 'not implemented'; },
    origin: () => { throw 'not implemented'; },
    returnToOrigin: () => { throw 'not implemented'; },
});
export const RoutingProvider = ({ strategy, children }) => {
    return React.createElement(RoutingStrategyContext.Provider, { 'value': strategy }, children);
};
export const useRoutingConfig = () => useContext(RoutingStrategyContext);
export const useRouting = () => {
    const strategy = useContext(RoutingStrategyContext);
    const [state, setState] = useState(() => strategy.current());
    useEffect(() => {
        setState(strategy.current());
    }, [strategy]);
    useEffect(() => strategy.listen(() => setState(strategy.current())), [strategy]);
    const navigate = useCallback((target, options) => strategy.navigate(target, options), [strategy]);
    return [state, navigate];
};
// While `when` holds, leaving the route waits on the screen's answer, the browser's back and forward
// included.
export const useNavigationBlocker = (when) => {
    const [pending, setPending] = useState(null);
    useEffect(() => {
        if (!when)
            return;
        return addBlocker(proceed => setPending(() => proceed));
    }, [when]);
    return {
        'blocked': pending !== null,
        'proceed': () => {
            setPending(null);
            pending === null || pending === void 0 ? void 0 : pending();
        },
        'cancel': () => setPending(null),
    };
};
export default useRouting;
